import {
  Component,
  EventEmitter,
  Input,
  Output,
  Renderer2,
  ViewChild,
  OnInit,
  AfterViewInit,
  OnDestroy,
  signal,
} from '@angular/core';
import { NgForm } from '@angular/forms';
import { Router } from '@angular/router';
import { Subject, debounceTime, takeUntil } from 'rxjs';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { EducatorSearchResultDto, GetEducatorSearchQuery, SearchClient } from 'src/shared/services/api.service';
import { DateService } from 'src/shared/services/date.service';
import { FeatureManagementService } from 'src/shared/services/feature-management.service';

@Component({
  selector: 'cert-lea-staff-search',
  templateUrl: './lea-staff-search.component.html',
  styleUrls: ['./lea-staff-search.component.scss'],
  standalone: false,
})
export class LeaStaffSearchComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('form', { static: true }) ngForm!: NgForm;

  searching = signal(false);
  scrollSearching = signal(false);
  formChangesSubscription: any;
  staff = signal<EducatorSearchResultDto[] | undefined>(undefined);
  dateOfBirth!: Date | undefined;

  obscurityFeatureActive = signal(false);

  @Input()
  showCreateButton = false;

  @Input()
  previousSearchRequest: GetEducatorSearchQuery = {
    pageNumber: 1,
    pageSize: 10,
  };

  private cancelSearch$ = new Subject<void>();

  searchRequest: GetEducatorSearchQuery = {
    pageNumber: 1,
    pageSize: 10,
  };

  endOfResults = signal(false);

  @Output() searchExecuted = new EventEmitter<LeaCreatePersonParams>();

  constructor(
    private readonly searchClient: SearchClient,
    private readonly dateService: DateService,
    private readonly activeModalService: NgbActiveModal,
    private readonly router: Router,
    private readonly renderer: Renderer2,
    private readonly featureManager: FeatureManagementService,
  ) {}

  ngOnInit() {
    if (this.previousSearchRequest) {
      this.searchRequest = this.previousSearchRequest;

      if (this.searchRequest.dateOfBirth) {
        this.dateOfBirth = this.searchRequest.dateOfBirth;
      }
    }

    this.formChangesSubscription = this.ngForm.form.valueChanges.pipe(debounceTime(1000)).subscribe((input) => {
      if (input.phoneNumber === '') {
        input.phoneNumber = null;
      }

      if (input.dateOfBirth && this.dateService.isValidDate(this.dateOfBirth)) {
        this.searchRequest.dateOfBirth = new Date(input.dateOfBirth);
      }

      if (!this.isInputEmpty(input)) {
        this.getSearchResults(input);
      } else {
        this.staff.set(undefined);
      }
    });

    this.featureManager.features.subscribe((features) => {
      this.obscurityFeatureActive.set(features.informationObscurity);
    });
  }

  ngAfterViewInit() {
    this.renderer.selectRootElement('#lastName').focus();
  }

  ngOnDestroy() {
    this.formChangesSubscription?.unsubscribe();
  }

  getSearchResults(searchRequest: GetEducatorSearchQuery) {
    this.endOfResults.set(false);
    this.cancelSearch$.next();

    this.searching.set(true);
    this.searchRequest.pageNumber = 1;

    if (this.dateOfBirth && this.dateService.isValidDate(this.dateOfBirth)) {
      searchRequest.dateOfBirth = this.dateOfBirth;
    } else {
      searchRequest.dateOfBirth = undefined;
    }

    this.searchClient
      .search(searchRequest)
      .pipe(takeUntil(this.cancelSearch$))
      .subscribe({
        next: (result) => {
          this.staff.set(result.educatorSearchResults);
          if (result.educatorSearchResults && result.educatorSearchResults.length < this.searchRequest.pageSize) {
            this.endOfResults.set(true);
          }
          this.searchExecuted.emit({ searchQuery: this.searchRequest, date: this.dateOfBirth });
        },
        complete: () => this.searching.set(false),
      });
  }

  onScroll() {
    if (!this.endOfResults()) {
      this.scrollSearching.set(true);
      this.searchRequest.pageNumber++;

      this.searchClient.search(this.searchRequest).subscribe({
        next: (result) => {
          if (result?.educatorSearchResults?.length) {
            this.endOfResults.set(false);
            this.staff.set(this.staff()?.concat(result.educatorSearchResults));

            if (result.educatorSearchResults.length < this.searchRequest.pageSize) {
              this.endOfResults.set(true);
            }
          }
        },
        complete: () => this.scrollSearching.set(false),
      });
    }
  }

  clearFormInputs() {
    this.cancelSearch$.next();

    this.searchRequest = {
      pageNumber: 1,
      pageSize: 10,
    };
    this.ngForm.reset();

    this.searching.set(false);
    this.endOfResults.set(false);
  }

  isInputEmpty(input: GetEducatorSearchQuery): boolean {
    if (this.doesMeetsSearchRequirements(input.lastName, 3)) return false;
    if (this.doesMeetsSearchRequirements(input.firstName, 2)) return false;
    if (this.doesMeetsSearchRequirements(input.middleName, 1)) return false;
    if (this.doesMeetsSearchRequirements(input.phoneNumber, 3)) return false;
    if (this.doesMeetsSearchRequirements(input.email, 3)) return false;
    if (this.doesMeetsSearchRequirements(input.ein, 7)) return false;
    if (this.doesMeetsSearchRequirements(input.ssn, 4)) return false;
    if (this.doesMeetsSearchRequirements(input.educatorStakeholderId, 4)) return false;
    if (
      input.dateOfBirth !== null &&
      input.dateOfBirth !== undefined &&
      input.dateOfBirth.toString().length > 8 &&
      !isNaN(new Date(input.dateOfBirth).getTime())
    )
      return false;

    return true;
  }

  doesMeetsSearchRequirements(input: string | undefined, length: number): boolean {
    return !input || (input && input!.length < length) ? false : true;
  }

  goToStaffDetails(personId: number) {
    this.activeModalService.close();
    this.router.navigateByUrl('/lea2/staff/staff-details/' + personId);
  }

  stopPropagation(event: MouseEvent): void {
    if (event) {
      event.stopPropagation();
    }
  }
}

export interface LeaCreatePersonParams {
  searchQuery: GetEducatorSearchQuery;
  date: Date | undefined;
}
